import React, { memo, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BudgetCard from 'routes/BudgetModule/components/BudgetProposal/shared/Card/BudgetCard';
import {
    PROJECT_NAME,
    INSCRIPTION_EXPENSE_INCOME,
} from 'routes/BudgetModule/config/constants';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { translateBlock } from 'i18n';
import { PivotFieldsAggridT } from 'business/definitions';
import {
    getProjectPivotGridDataQuery,
    getRejectedInscriptionFilters,
} from 'routes/BudgetModule/helpers/query';
import { getFieldForPhase } from 'routes/BudgetModule/shared/phases/helpers';
import { useIntegrationSoftwares } from 'helpers/integrations';
import { getProjectGridDimensions } from '../helpers/pivotGrid';
import ExportMenuContainer from 'components/ExportMenu/ExportMenuContainer';
import { SelectedChartForArgumentT } from '../Analyse';
import { removeEmptyInNotInFilters } from 'routes/BudgetModule/components/shared/Filters/helpers/filtersHelpers';
import { addProjectPivotGridToArgument } from '../helpers/addToArgument';
import ProjectsPivotGrid from './ProjectsPivotGrid';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';

type Props = {
    budgetProposal: BudgetProposalT;
    resourceSoftwareId: string;
    extraFilters: QueryFilterT[];
    settings: PivotFieldsAggridT | undefined;
    isLoading: boolean;
    onAddToArgumentModalOpen: (
        selectedChart: SelectedChartForArgumentT,
        indicatorName: string,
    ) => void;
};

const StyBudgetCardContainer = styled(Box, {
    name: 'StyBudgetCardContainer',
})({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%',
});

const ProjectsChart = ({
    budgetProposal,
    resourceSoftwareId,
    extraFilters,
    settings,
    isLoading: isLoadingProp,
    onAddToArgumentModalOpen,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const { data: integratedSoftwares, isLoading: areSoftwaresLoading } =
        useIntegrationSoftwares();

    const statusesSettings = useContext(StatusSettingsContext);

    const [exporterToExcel, setExporterToExcel] = useState<null | (() => void)>(
        null,
    );

    const { by, phase } = budgetProposal;

    const amountRequestedField = getFieldForPhase(phase, 'amount');

    const queryWithoutFilterDimensions = [
        PROJECT_NAME,
        INSCRIPTION_EXPENSE_INCOME,
        ...getProjectGridDimensions(integratedSoftwares),
    ].map((field) => ({
        field,
    }));
    const rawQuery: QueryT = {
        metrics: [{ field: amountRequestedField, aggregation: 'sum' }],
        dimensions: queryWithoutFilterDimensions,
        filters: removeEmptyInNotInFilters(
            getRejectedInscriptionFilters(phase, statusesSettings),
        ),
    };

    const projectPivotGridQuery = getProjectPivotGridDataQuery({
        rawQueryElements: rawQuery,
        budgetProposal,
        resourceSoftwareId,
        extraFilters,
    });

    const saveButtonTitle = t('budgetProposal.analyseSaveButtonTitle');
    const addToArgument = addProjectPivotGridToArgument({
        pivotTableTitle: t('budgetProposal.projectsPivotGrid'),
        projectPivotGridQuery,
        onAddToArgumentModalOpen,
        pivotFieldsAggridSettings: settings,
        indicatorName: 'projects_with_budget_inscriptions',
    });
    return (
        <StyBudgetCardContainer>
            <BudgetCard
                title={t('budgetProposal.projectsPivotGrid')}
                subtitle={t('budgetProposal.projectsPivotGridFilters', {
                    resourceType: translateBlock(by, true),
                })}
                sx={{ width: '100%', margin: '1%' }}
                exportMenu={
                    <ExportMenuContainer exportExcelData={exporterToExcel} />
                }
                saveButtonTitle={saveButtonTitle}
                onSubmit={addToArgument}
            >
                <ProjectsPivotGrid
                    budgetProposal={budgetProposal}
                    setExporterToExcel={setExporterToExcel}
                    filters={projectPivotGridQuery.filters}
                    isLoading={isLoadingProp || areSoftwaresLoading}
                    settings={settings}
                    integratedSoftwares={integratedSoftwares}
                />
            </BudgetCard>
        </StyBudgetCardContainer>
    );
};

export default memo(ProjectsChart);
